import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import MoleculeImage from './MoleculeImage';
import StructureEditor from './StructureEditor';
import API_BASE from '../config';
import { authHeaders, clearEditPassword } from '../services/auth';
import './ReactionDetail.css';

const API_BASE_URL = API_BASE;

const ReactionDetail = ({ editable = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reaction, setReaction] = useState(null);
  const [molecules, setMolecules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [pendingRoles, setPendingRoles] = useState({});
  const [metaPending, setMetaPending] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [addingMol, setAddingMol] = useState(false);
  const [newMolData, setNewMolData] = useState(null);
  const [newMolRole, setNewMolRole] = useState('reactant');
  const [addError, setAddError] = useState(null);
  const [reactionTypes, setReactionTypes] = useState([]);
  const [imageVersion, setImageVersion] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await api.getReaction(id);
        setReaction(data.reaction);
        setMolecules(data.molecules);
        if (editable) {
          const initial = {};
          data.molecules.forEach(m => { initial[m.id] = m.role; });
          setPendingRoles(initial);
          setMetaPending({
            reaction_type: data.reaction.reaction_type || '',
            reagent_table: data.reaction.reagent_table || '',
            url: data.reaction.url || '',
            experimental_procedure: data.reaction.experimental_procedure || '',
          });
          setEditMode(true);
        }
      } catch (err) {
        setError('Failed to load reaction: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/reaction-types`)
      .then(r => r.json())
      .then(data => setReactionTypes(data.filter(t => t.reaction_type).map(t => t.reaction_type)))
      .catch(() => {});
  }, []);

  const enterEditMode = () => {
    const initial = {};
    molecules.forEach(m => { initial[m.id] = m.role; });
    setPendingRoles(initial);
    setMetaPending({
      reaction_type: reaction.reaction_type || '',
      reagent_table: reaction.reagent_table || '',
      url: reaction.url || '',
      experimental_procedure: reaction.experimental_procedure || '',
    });
    setSaveError(null);
    setSaveSuccess(false);
    setEditMode(true);
  };

  const cancelEdit = () => {
    // Reset pending fields back to last saved values
    const initial = {};
    molecules.forEach(m => { initial[m.id] = m.role; });
    setPendingRoles(initial);
    setMetaPending({
      reaction_type: reaction.reaction_type || '',
      reagent_table: reaction.reagent_table || '',
      url: reaction.url || '',
      experimental_procedure: reaction.experimental_procedure || '',
    });
    setSaveError(null);
    if (!editable) setEditMode(false);
  };

  const deleteReaction = async () => {
    if (!window.confirm(`Delete reaction #${reaction.record_number} and all its structures? This cannot be undone.`)) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/reactions/${reaction.record_number}`, { method: 'DELETE', headers: authHeaders() });
      if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Delete failed');
      }
      navigate('/edit');
    } catch (err) {
      setSaveError(err.message);
    }
  };

  const handleRoleChange = (molId, newRole) => {
    setPendingRoles(prev => ({ ...prev, [molId]: newRole }));
  };

  const deleteMolecule = async (mol) => {
    if (!window.confirm(`Delete this ${mol.role}? This cannot be undone.`)) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/molecules/${mol.id}`, { method: 'DELETE', headers: authHeaders() });
      if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Delete failed');
      const updated = await api.getReaction(id);
      setMolecules(updated.molecules);
      setPendingRoles(prev => {
        const next = { ...prev };
        delete next[mol.id];
        return next;
      });
    } catch (err) {
      setSaveError(err.message);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      // Save meta if anything changed
      const metaChanged =
        (metaPending.reaction_type || '') !== (reaction.reaction_type || '') ||
        (metaPending.reagent_table || '') !== (reaction.reagent_table || '') ||
        (metaPending.url || '') !== (reaction.url || '') ||
        (metaPending.experimental_procedure || '') !== (reaction.experimental_procedure || '');

      if (metaChanged) {
        const resp = await fetch(`${API_BASE_URL}/reactions/${reaction.record_number}`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify(metaPending),
        });
        if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Save failed');
      }

      // Save changed roles
      const changed = molecules.filter(m => pendingRoles[m.id] !== m.role);
      for (const mol of changed) {
        const resp = await fetch(`${API_BASE_URL}/molecules/${mol.id}/role`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({ role: pendingRoles[mol.id] }),
        });
        if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Save failed');
      }

      const updated = await api.getReaction(id);
      setReaction(updated.reaction);
      setMolecules(updated.molecules);
      if (editable) {
        const initial = {};
        updated.molecules.forEach(m => { initial[m.id] = m.role; });
        setPendingRoles(initial);
        setMetaPending({
          reaction_type: updated.reaction.reaction_type || '',
          reagent_table: updated.reaction.reagent_table || '',
          url: updated.reaction.url || '',
          experimental_procedure: updated.reaction.experimental_procedure || '',
        });
      } else {
        setEditMode(false);
        setPendingRoles({});
        setMetaPending({});
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStructureSave = async (newSmiles, newMolblock) => {
    const mol = editingStructure;
    try {
      const resp = await fetch(`${API_BASE_URL}/molecules/${mol.id}/structure`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ smiles: newSmiles, mol_block: newMolblock }),
      });
      if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Save failed');
      const updated = await api.getReaction(id);
      setMolecules(updated.molecules);
      setImageVersion(v => v + 1);
      setEditingStructure(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err.message);
      setEditingStructure(null);
    }
  };

  const handleNewMolDrawn = (smiles, molblock) => {
    setAddingMol(false);
    setNewMolData({ smiles, molblock });
    setNewMolRole('reactant');
    setAddError(null);
  };

  const confirmAddMolecule = async () => {
    if (!newMolData) return;
    setSaving(true);
    setAddError(null);
    try {
      const resp = await fetch(`${API_BASE_URL}/reactions/${reaction.id}/molecules`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ smiles: newMolData.smiles, mol_block: newMolData.molblock, role: newMolRole }),
      });
      if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Add failed');
      const updated = await api.getReaction(id);
      setMolecules(updated.molecules);
      setNewMolData(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setAddError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading reaction...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!reaction) return <div className="error">Reaction not found.</div>;

  const displayMols = editMode
    ? molecules.map(m => ({ ...m, role: pendingRoles[m.id] ?? m.role }))
    : molecules;

  const reactants = displayMols.filter(m => m.role === 'reactant');
  const reagents  = displayMols.filter(m => m.role === 'reagent');
  const products  = displayMols.filter(m => m.role === 'product');

  return (
    <div className="detail-container">
      <div className="detail-header">
        <Link to={editable ? '/edit' : '/'} className="back-link">← Back to list</Link>
        <div className="detail-title">
          <h1>Reaction #{reaction.record_number}</h1>
          {editMode ? (
            <>
              <input
                className="meta-type-input"
                value={metaPending.reaction_type}
                onChange={e => setMetaPending(p => ({ ...p, reaction_type: e.target.value }))}
                placeholder="Reaction type"
                list="reaction-type-options"
              />
              <datalist id="reaction-type-options">
                {reactionTypes.map(t => <option key={t} value={t} />)}
              </datalist>
            </>
          ) : (
            <span className="reaction-type-badge">{reaction.reaction_type}</span>
          )}
          {editable && (
            <button className="delete-reaction-btn" onClick={deleteReaction}>Delete Reaction</button>
          )}
        </div>
      </div>

      {saveSuccess && (
        <div className="save-success">Saved.</div>
      )}

      <div className="reaction-scheme-full">
        {reactants.map((mol, i) => (
          <div key={i} className="molecule-group-full">
            <div className="group-label">Reactant</div>
            <div className="molecule-cell">
              <MoleculeImage molId={mol.id} smiles={mol.smiles} width={240} height={240} version={imageVersion} />
            </div>
          </div>
        ))}
        <div className="scheme-arrow">
          {reagents.map((mol, i) => (
            <div key={i} className="molecule-group-full">
              <div className="group-label">Reagent</div>
              <div className="molecule-cell">
                <MoleculeImage molId={mol.id} smiles={mol.smiles} width={160} height={160} version={imageVersion} />
              </div>
            </div>
          ))}
          <div className="arrow-line">→</div>
        </div>
        {products.map((mol, i) => (
          <div key={i} className="molecule-group-full">
            <div className="group-label">Product</div>
            <div className="molecule-cell">
              <MoleculeImage molId={mol.id} smiles={mol.smiles} width={240} height={240} version={imageVersion} />
            </div>
          </div>
        ))}
      </div>

      <div className="detail-metadata">
        {editMode ? (
          <>
            <div className="metadata-section">
              <h3>Reagents / Conditions</h3>
              <textarea
                className="meta-textarea"
                value={metaPending.reagent_table}
                onChange={e => setMetaPending(p => ({ ...p, reagent_table: e.target.value }))}
                rows={5}
                placeholder="Reagents / conditions…"
              />
            </div>
            <div className="metadata-section">
              <h3>Experimental Procedure</h3>
              <textarea
                className="meta-textarea meta-textarea-procedure"
                value={metaPending.experimental_procedure}
                onChange={e => setMetaPending(p => ({ ...p, experimental_procedure: e.target.value }))}
                rows={8}
                placeholder="Experimental procedure…"
              />
            </div>
            <div className="metadata-section">
              <h3>Reference</h3>
              <input
                className="meta-url-input"
                type="text"
                value={metaPending.url}
                onChange={e => setMetaPending(p => ({ ...p, url: e.target.value }))}
                placeholder="Reference URL"
              />
            </div>
          </>
        ) : (
          <>
            {reaction.reagent_table && (
              <div className="metadata-section">
                <h3>Reagents / Conditions</h3>
                <pre className="reagent-table-text">{reaction.reagent_table}</pre>
              </div>
            )}
            {reaction.experimental_procedure && (
              <div className="metadata-section">
                <h3>Experimental Procedure</h3>
                <pre className="reagent-table-text procedure-text">{reaction.experimental_procedure}</pre>
              </div>
            )}
            {reaction.url && (
              <div className="metadata-section">
                <h3>Reference</h3>
                <a href={reaction.url} target="_blank" rel="noopener noreferrer" className="ref-link">
                  {reaction.url}
                </a>
              </div>
            )}
          </>
        )}
      </div>

      <div className="molecules-detail">
        <div className="molecules-detail-header">
          <h3>All Molecules</h3>
          {editable && !newMolData && (
            <button className="add-mol-btn" onClick={() => setAddingMol(true)}>
              + Add Molecule
            </button>
          )}
        </div>

        {newMolData && (
          <div className="add-mol-confirm">
            <MoleculeImage smiles={newMolData.smiles} width={160} height={160} />
            <div className="add-mol-confirm-controls">
              <label>Role:</label>
              <select
                className="role-select"
                value={newMolRole}
                onChange={e => setNewMolRole(e.target.value)}
              >
                <option value="reactant">reactant</option>
                <option value="reagent">reagent</option>
                <option value="product">product</option>
              </select>
              <button className="save-btn" onClick={confirmAddMolecule} disabled={saving}>
                {saving ? 'Adding…' : 'Confirm Add'}
              </button>
              <button className="cancel-btn" onClick={() => { setNewMolData(null); setAddError(null); }} disabled={saving}>
                Cancel
              </button>
              <button className="edit-structure-btn" onClick={() => { setAddingMol(true); setNewMolData(null); }} disabled={saving}>
                Redraw
              </button>
              {addError && <span className="save-error">{addError}</span>}
            </div>
          </div>
        )}

        <div className="molecules-table">
          {displayMols.map((mol) => (
            <div key={mol.id} className="molecule-row">
              <div className="molecule-image-cell">
                <MoleculeImage molId={mol.id} smiles={mol.smiles} width={180} height={180} version={imageVersion} />
              </div>
              <div className="molecule-info-cell">
                {editMode ? (
                  <select
                    className="role-select"
                    value={pendingRoles[mol.id] ?? mol.role}
                    onChange={e => handleRoleChange(mol.id, e.target.value)}
                  >
                    <option value="reactant">reactant</option>
                    <option value="reagent">reagent</option>
                    <option value="product">product</option>
                  </select>
                ) : (
                  <span className={`role-badge role-${mol.role}`}>{mol.role}</span>
                )}
                <div className="smiles-text">{mol.smiles}</div>
                {editable && (
                  <div className="edit-row-controls">
                    <button className="edit-structure-btn" onClick={() => setEditingStructure(mol)}>
                      Edit Structure
                    </button>
                    <button className="delete-mol-btn" onClick={() => deleteMolecule(mol)}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {editMode && (
          <div className="edit-controls">
            <button className="save-btn" onClick={saveAll} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button className="cancel-btn" onClick={cancelEdit} disabled={saving}>
              {editable ? 'Reset' : 'Cancel'}
            </button>
            {saveError && <span className="save-error">{saveError}</span>}
          </div>
        )}
      </div>

      {editingStructure && (
        <StructureEditor
          molblock={editingStructure.mol_block}
          smiles={editingStructure.smiles}
          onSave={handleStructureSave}
          onClose={() => setEditingStructure(null)}
        />
      )}
      {addingMol && (
        <StructureEditor
          onSave={handleNewMolDrawn}
          onClose={() => setAddingMol(false)}
        />
      )}
    </div>
  );
};

export default ReactionDetail;
